import L from "leaflet";

import { _lcs } from "../i18n";

/**
 * MapView — the BELTURO basemap, wrapped once for the whole guest app.
 *
 * The provider is OpenTopoMap (see config.json → `map`): OpenStreetMap-derived
 * topographic raster tiles, free for commercial use under CC-BY-SA, no key and
 * no account. That matters for a guest app in Germany specifically — a Google
 * Maps embed loads a US-hosted script and sets identifiers before a guest has
 * agreed to anything, which is a consent banner in front of a map somebody just
 * wanted to glance at. Attribution is a licence condition, not decoration, and
 * Leaflet renders it in the corner for us.
 *
 * Everything the screens need goes through here so the tile URL, the pin markup
 * and the two Leaflet traps are in one place:
 *
 *  1. Leaflet measures its container when the map is created. A container that
 *     is `hidden`, or in a tab that has not been shown, measures 0×0 and the
 *     tiles are then laid out against the wrong size for good — hence
 *     `invalidate()`, and why callers create the map only once it is visible.
 *  2. The default marker pulls PNGs by a path resolved at runtime, which a
 *     bundler breaks. Every pin here is a `divIcon` — CSS on the design tokens.
 */
export class MapView
{
    /** Where the map opens when there is nothing to fit. */
    static DEFAULT_ZOOM = 13;

    /** True when a tile source is configured — nothing renders without one. */
    static isAvailable( app )
    {
        return !!( app.config.get( "map" ) && app.config.get( "map" ).tileUrl );
    }

    /**
     * @param {IF.App} app
     * @param {HTMLElement} container Must already be laid out (visible, sized).
     */
    constructor( app, container )
    {
        this.app = app;
        this.config = app.config.get( "map" ) || {};

        this.map = L.map( container, {
            // The guest scrolls the page with the same finger; a map that
            // grabbed the wheel/drag would trap them. Pinch and the +/− buttons
            // still zoom.
            "scrollWheelZoom" : false,
            "zoomControl" : false,
            "attributionControl" : true
        } );

        L.tileLayer( this.config.tileUrl, {
            "attribution" : this.config.attribution || "",
            "maxZoom" : +this.config.maxZoom || 17
        } ).addTo( this.map );

        this._markers = [];
        this._track = null;
        this._here = null;
        // The framing fit() chose, so reset() can return to exactly it.
        this._home = null;
    }

    /* -------------------------------- Content -------------------------------- */

    /**
     * A pin for one card.
     *
     * @param {Object} card Region card ({ id, kind, title, icon, latitude, longitude })
     * @param {Function} [onSelect] Called with the card when the pin is tapped
     * @returns {Object|null} the Leaflet marker, or null without coordinates
     */
    addPin( card, onSelect = null )
    {
        if ( card.latitude === null || card.longitude === null )
        {
            return null;
        }

        const marker = L.marker( [ card.latitude, card.longitude ], {
            "icon" : this._pinIcon( card, false ),
            "title" : card.title || "",
            "riseOnHover" : true
        } ).addTo( this.map );

        marker._btCard = card;
        this._markers.push( marker );

        if ( onSelect )
        {
            marker.on( "click", () => onSelect( card ) );
        }

        return marker;
    }

    /** Grow the selected pin and shrink the previous one. Null clears. */
    setSelected( cardId )
    {
        this._markers.forEach( ( marker ) =>
        {
            const isSelected = marker._btCard.id === cardId;
            marker.setIcon( this._pinIcon( marker._btCard, isSelected ) );
            marker.setZIndexOffset( isSelected ? 1000 : 0 );
        } );
    }

    _pinIcon( card, isSelected )
    {
        const size = isSelected ? 40 : 32;
        // gastro / poi / tour — the three things a pin can be, so the guest can
        // tell a restaurant from a viewpoint without tapping it.
        const modifier = card.kind === "tour" ? "tour" : ( card.type || "gastro" );

        return L.divIcon( {
            "className" : "",
            "html" :
                '<span class="rgmap__pin rgmap__pin--' + modifier +
                    ( isSelected ? " rgmap__pin--on" : "" ) + '">' +
                    '<i class="icon icon--' + ( card.icon || "map-pin" ) + '"></i>' +
                '</span>',
            "iconSize" : [ size, size ],
            "iconAnchor" : [ size / 2, size / 2 ]
        } );
    }

    /** The house — a dot, not a pin: it is where the guest is, not a target. */
    addHere( latitude, longitude, label = null )
    {
        if ( !Number.isFinite( +latitude ) || !Number.isFinite( +longitude ) )
        {
            return null;
        }

        this._here = L.marker( [ +latitude, +longitude ], {
            "icon" : L.divIcon( {
                "className" : "",
                "html" : '<span class="rgmap__here"></span>',
                "iconSize" : [ 16, 16 ],
                "iconAnchor" : [ 8, 8 ]
            } ),
            "keyboard" : false,
            "interactive" : false
        } ).addTo( this.map );

        this._here.bindTooltip( label || _lcs( "Dein Aufenthalt" ),
            { "direction" : "top", "offset" : [ 0, -10 ] } );
        return this._here;
    }

    /**
     * The track of a tour.
     * @param {Array} coordinates GeoJSON [ [lon, lat], … ] — flipped here.
     */
    addTrack( coordinates )
    {
        const points = ( coordinates || [] )
            .filter( ( c ) => Array.isArray( c ) && Number.isFinite( +c[ 0 ] ) && Number.isFinite( +c[ 1 ] ) )
            .map( ( [ lon, lat ] ) => [ +lat, +lon ] );

        if ( points.length < 2 )
        {
            return null;
        }

        this._track = L.polyline( points, {
            "color" : "#2f6f4e",
            "weight" : 4,
            "opacity" : 0.9,
            "lineJoin" : "round",
            "lineCap" : "round"
        } ).addTo( this.map );

        return this._track;
    }

    /* -------------------------------- Controls ------------------------------- */

    /**
     * Wire a screen's zoom / recentre buttons — `[data-map="in|out|reset"]` —
     * to whichever map is currently up.
     *
     * Leaflet's own zoom control is off (`zoomControl: false`) so both maps can
     * use the app's buttons instead of two different-looking pairs of `+`/`−`.
     *
     * The map is resolved lazily through `resolve` rather than passed in,
     * because the buttons are in the markup before the map exists — and on the
     * Region tab the map is torn down and rebuilt whenever the category changes.
     *
     * @param {IF.State} state Owns the listeners (tracked cleanup on exit)
     * @param {HTMLElement} root Element containing the buttons
     * @param {Function} resolve Returns the live MapView, or null
     */
    static bindControls( state, root, resolve )
    {
        root.querySelectorAll( "[data-map]" ).forEach( ( button ) =>
        {
            state.addEventListener( button, "click", () =>
            {
                const map = resolve();
                if ( !map )
                {
                    return;
                }

                const action = button.getAttribute( "data-map" );
                if ( action === "in" )    map.zoomIn();
                if ( action === "out" )   map.zoomOut();
                if ( action === "reset" ) map.reset();
            } );
        } );
    }

    /* --------------------------------- View ---------------------------------- */

    /**
     * Frame everything that has been added, and remember that framing so
     * `reset()` can return to it — whichever branch below produced it. A
     * `reset` that only knew about the fitted-bounds case would silently
     * recentre a place detail on the HOUSE instead of on the place.
     */
    fit( padding = 34 )
    {
        const layers = this._markers.concat( this._track ? [ this._track ] : [] );

        if ( layers.length === 0 )
        {
            const center = this._here ? this._here.getLatLng() : L.latLng( 51.0, 13.7 );
            this._home = { "center" : center, "zoom" : MapView.DEFAULT_ZOOM };
        }
        else
        {
            const bounds = L.featureGroup( layers ).getBounds();

            // A single pin has no extent; fitBounds on it would zoom to the
            // tile grid's maximum and show four roof tiles.
            this._home = ( layers.length === 1 && !this._track )
                ? { "center" : bounds.getCenter(), "zoom" : MapView.DEFAULT_ZOOM }
                : { "bounds" : bounds, "padding" : padding };
        }

        this._applyHome();
    }

    center( latitude, longitude, zoom = MapView.DEFAULT_ZOOM )
    {
        this.map.setView( [ +latitude, +longitude ], zoom );
    }

    zoomIn()  { this.map.zoomIn(); }
    zoomOut() { this.map.zoomOut(); }

    /** Back to the framing `fit()` chose — the "wo bin ich wieder" button. */
    reset()
    {
        this._applyHome();
    }

    _applyHome()
    {
        if ( !this._home )
        {
            return;
        }

        if ( this._home.bounds )
        {
            this.map.fitBounds( this._home.bounds, {
                "padding" : [ this._home.padding, this._home.padding ]
            } );
            return;
        }

        this.map.setView( this._home.center, this._home.zoom );
    }

    /**
     * Re-measure. Needed after the container changes size or becomes visible —
     * switching from the list view to the map is exactly that.
     */
    invalidate()
    {
        this.map.invalidateSize();
    }

    destroy()
    {
        try { this.map.remove(); } catch ( e ) { /* the container is going away anyway */ }
        this.map = null;
        this._markers = [];
        this._track = null;
        this._here = null;
        this._home = null;
    }
}
